import React, { useState } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import { formatCurrency } from 'utils';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import InputAdornment from '@material-ui/core/InputAdornment';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';

const PlanLabel = {
  'Tier': 'Deferred Success',
  'Recurring': 'Installment',
  'One time': 'Prepaid',
  'Perpetual': 'Subscription',
};

const useStyles = makeStyles((theme) => ({
  field: {
    width: 200,
  },
  inputRoot: {
    background: theme.palette.action.disabledBackground,
  },
  notchedOutline: {
    borderColor: 'transparent',
  },
  inputFocused: {
    borderColor: 'transparent',
    backgroundColor: theme.palette.primary.light,
  },
}));

const calcInstallment = (total, months) => {
  return +(total / months).toFixed(2);
};

const CartSummary = ({
  plan,
  couponCode,
  coupon,
  onCouponChange,
  onSubmitCoupon,
}) => {
  const classes = useStyles();
  const theme = useTheme()
  const { package_name, pricing_model, total, signup_fee } = plan;
  const [showCoupon, setShowCoupon] = useState(!!coupon);

  const calcDiscount = (amnt) => {
    let discount = 0;
    if (coupon) {
      if (coupon.percent_off) {
        discount = amnt * (coupon.percent_off / 100);
      } else if (coupon.amount_off) {
        discount = coupon.amount_off / 100;
      }
    }
    return discount;
  };

  const installment =
    pricing_model === 'Recurring' && calcInstallment(total, plan.installments);

  /*
    If the discount is fixed amount then the discount should apply for first invoice only.
    For % discount, discount will be applied over the subscription duration per invoice
   */
  const calInstallmentAmount = () => {
    const amount = calcInstallment(total, plan.installments);
    if (coupon?.amount_off) {
      return amount;
    }
    return amount - calcDiscount(amount);
  };

  const discount = calcDiscount(total);

  const getTotal = () => {
    if (pricing_model === 'Recurring') {
      return coupon?.amount_off
        ? installment - discount
        : calInstallmentAmount();
    } else {
      return total - discount;
    }
  };

  const dueToday = signup_fee + getTotal();

  return (
    <Box>
      <Typography paragraph variant="subtitle1">{package_name}</Typography>
      <ul className="square">
        {pricing_model !== 'Perpetual' && (
          <li>
            <Box display="flex" justifyContent="space-between" marginTop={3}>
              <Typography className={classes.field}>Payment Plan:</Typography>
              <Typography>{PlanLabel[pricing_model]}</Typography>
            </Box>
          </li>
        )}
        {pricing_model === 'Recurring' && (
          <li>
            <Typography>
              You will be billed{' '}
              <strong>{formatCurrency(calInstallmentAmount())}</strong> per
              month for <strong>{plan.installments} months.</strong>
            </Typography>
          </li>
        )}
        <li>
          {pricing_model === 'Recurring' ? (
            <Box display="flex" justifyContent="space-between">
              <Typography>Plan Total:</Typography>
              <Typography variant="body2">
                {formatCurrency(total)}
              </Typography>
            </Box>
          ) : (
            <Box display="flex" justifyContent="space-between">
              <Typography>
                {pricing_model === 'Tier' ? 'Initial Payment:' : 'Program Fee:'}
              </Typography>
              <Typography variant="body2">
                {formatCurrency(total)}{pricing_model === 'Perpetual' && '/month'}
              </Typography>
            </Box>
          )}
        </li>
        {discount ? (
          <li>
            <Box display="flex" justifyContent="space-between">
              <Typography>Discount ({coupon.name}):</Typography>
              <Typography variant="body2">
                {formatCurrency(discount)}
              </Typography>
            </Box>
          </li>
        ) : null}
        <li>
          <Box display="flex" justifyContent="space-between">
            <Typography>Non-Refundable Registration Fee:</Typography>
            <Typography variant="body2">
              {formatCurrency(signup_fee)}
            </Typography>
          </Box>
        </li>
        <li>
          <Box display="flex" justifyContent="space-between">
            <Typography variant="body2" color="primary">
              Amount Due {plan.trial_days ? `after ${plan.trial_days} day trial` : 'Today'}:
            </Typography>
            <Typography variant="body2" color="primary">
              {formatCurrency(dueToday)}
            </Typography>
          </Box>
        </li>
      </ul>
      {onCouponChange && (
        <Box marginTop={6} marginBottom={3}>
          <OutlinedInput
            fullWidth
            classes={{
              root: classes.inputRoot,
              notchedOutline: classes.notchedOutline,
              focused: classes.inputFocused,
            }}
            size="large"
            placeholder="Do you have a coupon?"
            value={couponCode}
            onChange={onCouponChange}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  color="primary"
                  disabled={!couponCode}
                  onClick={onSubmitCoupon}
                >
                  <ArrowForwardIcon />
                </IconButton>
              </InputAdornment>
            }
          />
        </Box>
      )}
    </Box>
  );
};

export default CartSummary;
